/**
 * VersionDiff — Phase 5 View 1 (diff sub-route).
 *
 * Phase 3's diff endpoint returns concept *names* (not full concept records),
 * a flat field-delta map per modified concept (e.g. {entity_type: {base, target}}),
 * and a numeric metric_deltas map. We render against that shape directly —
 * no full concept hydration required.
 */
import { Link, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  GitCompare,
  Loader2,
  Plus,
  Minus,
  Pencil,
} from 'lucide-react';
import { clsx } from 'clsx';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  ReferenceLine,
} from 'recharts';
import { pdoLifecycleApi, type PdoVersionDiffResponse } from '@/services/api';
import { EmptyState } from '@/components/pdo/EmptyState';
import { ErrorBanner } from '@/components/pdo/ErrorBanner';
import { useResolvedClientUid, useFetch, formatNumber } from '@/components/pdo/utils';

export function VersionDiff() {
  const clientUid = useResolvedClientUid('/admin/ontology');
  const [searchParams] = useSearchParams();
  const v1 = searchParams.get('v1');
  const v2 = searchParams.get('v2');

  const diff = useFetch(
    () =>
      clientUid && v1 && v2
        ? pdoLifecycleApi.diffVersions(clientUid, v1, v2)
        : Promise.resolve(null as unknown as PdoVersionDiffResponse),
    [clientUid, v1, v2],
  );

  if (!clientUid) {
    return (
      <div className="flex items-center justify-center h-64 text-sm text-slate-500">
        <Loader2 size={16} className="animate-spin mr-2" /> Resolving client…
      </div>
    );
  }

  if (!v1 || !v2) {
    return (
      <EmptyState
        title="Pick two versions to compare"
        hint="Use the checkboxes on the version list, then click Compare."
        action={
          <Link
            to={`/admin/ontology/${clientUid}/versions`}
            className="text-xs font-medium text-teal-700 hover:underline"
          >
            ← Back to versions
          </Link>
        }
      />
    );
  }

  return (
    <div className="flex flex-col h-full">
      <header className="flex items-start justify-between gap-4 px-1 pb-4 border-b border-slate-200 mb-4">
        <div className="flex items-center gap-3">
          <Link
            to={`/admin/ontology/${clientUid}/versions`}
            className="flex items-center gap-1 text-sm text-slate-500 hover:text-navy-900"
          >
            <ArrowLeft size={14} />
            Versions
          </Link>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-brand-teal to-brand-green flex items-center justify-center text-navy-900 shadow-glow">
              <GitCompare size={18} />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-navy-900">Version Diff</h1>
              <p className="text-sm text-slate-500 mt-0.5 font-mono">
                {v1} <span className="text-slate-400">→</span> {v2}
              </p>
            </div>
          </div>
        </div>
      </header>

      {diff.error && <ErrorBanner error={diff.error} onRetry={diff.refetch} />}

      {diff.loading && !diff.data ? (
        <div className="flex items-center justify-center py-16 text-xs text-slate-400">
          <Loader2 size={14} className="animate-spin mr-2" />
          Computing diff…
        </div>
      ) : diff.data ? (
        <DiffBody diff={diff.data} />
      ) : null}
    </div>
  );
}

function DiffBody({ diff }: { diff: PdoVersionDiffResponse }) {
  const summaryTiles = [
    { label: 'Added', value: diff.concepts_added.length, tone: 'emerald' },
    { label: 'Removed', value: diff.concepts_removed.length, tone: 'red' },
    { label: 'Modified', value: diff.concepts_modified.length, tone: 'amber' },
    { label: 'Edges +', value: diff.edges_added, tone: 'emerald' },
    { label: 'Edges −', value: diff.edges_removed, tone: 'red' },
  ] as const;

  const metricRows = Object.entries(diff.metric_deltas).map(([metric, delta]) => ({
    metric,
    delta,
  }));

  return (
    <div className="space-y-5">
      {/* Summary tiles */}
      <div className="grid grid-cols-5 gap-2">
        {summaryTiles.map((t) => (
          <div
            key={t.label}
            className={clsx(
              'border rounded-lg px-3 py-2',
              t.tone === 'emerald' && 'border-emerald-200 bg-emerald-50',
              t.tone === 'red' && 'border-red-200 bg-red-50',
              t.tone === 'amber' && 'border-amber-200 bg-amber-50',
            )}
          >
            <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-600">
              {t.label}
            </p>
            <p
              className={clsx(
                'text-base font-mono font-semibold mt-0.5',
                t.tone === 'emerald' && 'text-emerald-700',
                t.tone === 'red' && 'text-red-700',
                t.tone === 'amber' && 'text-amber-700',
              )}
            >
              {formatNumber(t.value)}
            </p>
          </div>
        ))}
      </div>

      {/* Metric delta chart */}
      {metricRows.length > 0 && (
        <section className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
          <h3 className="text-sm font-semibold text-navy-900 mb-3">Metric deltas</h3>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={metricRows}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="metric" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip
                  contentStyle={{
                    fontSize: 12,
                    borderRadius: 8,
                    border: '1px solid #e2e8f0',
                  }}
                />
                <ReferenceLine y={0} stroke="#475569" />
                <Bar
                  dataKey="delta"
                  fill="#2dd4bf"
                  radius={[4, 4, 0, 0]}
                  isAnimationActive={false}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </section>
      )}

      <DiffSection
        icon={<Plus size={14} className="text-emerald-600" />}
        title={`Concepts added (${diff.concepts_added.length})`}
        tone="emerald"
        emptyHint="No new concepts in this diff."
        items={diff.concepts_added.map((name) => (
          <NameRow key={`add-${name}`} name={name} accent="emerald" />
        ))}
      />

      <DiffSection
        icon={<Minus size={14} className="text-red-600" />}
        title={`Concepts removed (${diff.concepts_removed.length})`}
        tone="red"
        emptyHint="No concepts dropped in this diff."
        items={diff.concepts_removed.map((name) => (
          <NameRow key={`rem-${name}`} name={name} accent="red" />
        ))}
      />

      <DiffSection
        icon={<Pencil size={14} className="text-amber-600" />}
        title={`Concepts modified (${diff.concepts_modified.length})`}
        tone="amber"
        emptyHint="No concept fields changed in this diff."
        items={diff.concepts_modified.map((m, i) => (
          <ModifiedConceptRow key={`mod-${m.name ?? i}`} item={m} />
        ))}
      />
    </div>
  );
}

function DiffSection({
  icon,
  title,
  tone,
  emptyHint,
  items,
}: {
  icon: React.ReactNode;
  title: string;
  tone: 'emerald' | 'red' | 'amber';
  emptyHint: string;
  items: React.ReactNode[];
}) {
  return (
    <section className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
      <header
        className={clsx(
          'flex items-center gap-2 px-4 py-2.5 border-b border-slate-200',
          tone === 'emerald' && 'bg-emerald-50/50',
          tone === 'red' && 'bg-red-50/50',
          tone === 'amber' && 'bg-amber-50/50',
        )}
      >
        {icon}
        <h3 className="text-sm font-semibold text-navy-900">{title}</h3>
      </header>
      {items.length === 0 ? (
        <EmptyState compact title={emptyHint} />
      ) : (
        <ul className="divide-y divide-slate-100 max-h-[420px] overflow-y-auto">
          {items.map((it, i) => (
            <li key={i}>{it}</li>
          ))}
        </ul>
      )}
    </section>
  );
}

function NameRow({
  name,
  accent,
}: {
  name: string;
  accent: 'emerald' | 'red';
}) {
  return (
    <div
      className={clsx(
        'flex items-center gap-3 px-4 py-2 hover:bg-slate-50',
        accent === 'emerald' && 'bg-emerald-50/30',
        accent === 'red' && 'bg-red-50/30',
      )}
    >
      <span className="text-sm font-medium text-navy-900 truncate">{name}</span>
    </div>
  );
}

function ModifiedConceptRow({
  item,
}: {
  item: import('@/services/api').PdoConceptModification;
}) {
  // Phase 3 emits {name, <field>: {base, target}, ...} per modified concept.
  const fieldEntries = Object.entries(item).filter(([k]) => k !== 'name');

  return (
    <div className="px-4 py-3 bg-amber-50/30 hover:bg-amber-50/50">
      <p className="text-sm font-medium text-navy-900 mb-1.5">{item.name}</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1 text-xs">
        {fieldEntries.length === 0 ? (
          <p className="text-slate-400 italic">No field-level changes recorded</p>
        ) : (
          fieldEntries.map(([field, change]) => {
            const c = change as { base?: unknown; target?: unknown } | undefined;
            return (
              <div key={field} className="flex items-center gap-2">
                <span className="text-slate-500 font-mono w-24 truncate">
                  {field}
                </span>
                <span className="text-slate-400 line-through font-mono">
                  {String(c?.base ?? '–')}
                </span>
                <span className="text-slate-400">→</span>
                <span className="text-amber-700 font-mono">
                  {String(c?.target ?? '–')}
                </span>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
